import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { ApiError } from "../api.js";
import { useAsync } from "../useAsync.js";
import { neckDiagramPreference, saveNeckDiagramPreference, type NeckDiagramPreference } from "./api.js";
import { NeckDiagramStudentContext } from "./NeckDiagramViewing.js";
const PreferenceContext = createContext<{ data: NeckDiagramPreference | null; save: (value: NeckDiagramPreference["handedness"]) => Promise<void>; busy: boolean; problem: string | null; reload: () => void } | null>(null);

export function NeckDiagramStudentView({ studentId, children }: { studentId?: string | null; children: ReactNode }) {
  const parent = useContext(NeckDiagramStudentContext);
  if (parent.studentId && (!studentId || parent.studentId === studentId)) return <>{children}</>;
  return <StudentPreference studentId={studentId ?? null}>{children}</StudentPreference>;
}
function StudentPreference({ studentId, children }: { studentId: string | null; children: ReactNode }) {
  const [needed, setNeeded] = useState(false);
  const activate = useCallback(() => setNeeded(true), []);
  const result = useAsync(async () => {
    if (!needed || !studentId) return { studentId, data: null };
    try { return { studentId, data: await neckDiagramPreference(studentId) }; }
    catch (error) { if (error instanceof ApiError && error.status === 404) return { studentId, data: null }; throw error; }
  }, [studentId, needed]);
  const [busy, setBusy] = useState(false), [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ studentId: string; data: NeckDiagramPreference } | null>(null);
  useEffect(() => { setSaved(null); setProblem(null); }, [studentId]);
  const data = saved?.studentId === studentId ? saved.data : result.data?.studentId === studentId ? result.data.data : null;
  async function save(value: NeckDiagramPreference["handedness"]) {
    if (!data || !studentId || busy) return;
    setBusy(true); setProblem(null);
    try { setSaved({ studentId, data: await saveNeckDiagramPreference(studentId, value, data.previousHandedness) }); }
    catch (error) { setProblem(error instanceof Error ? error.message : "Could not save the student's setting."); setSaved(null); result.reload(); }
    finally { setBusy(false); }
  }
  return <PreferenceContext.Provider value={{ data, save, busy, problem, reload: result.reload }}>
    <NeckDiagramStudentContext.Provider value={{ studentId, leftHanded: data?.handedness === "left", pending: !!studentId && !result.error && (!needed || (!data && (result.loading || result.data?.studentId !== studentId))), error: studentId ? result.error : null, activate, retry: result.reload }}>
      {children}
    </NeckDiagramStudentContext.Provider>
  </PreferenceContext.Provider>;
}
export function NeckDiagramHandedness() {
  const state = useContext(PreferenceContext);
  const { activate, error } = useContext(NeckDiagramStudentContext);
  useEffect(activate, [activate]);
  if (error) return <div className="neck-handedness"><p role="alert" className="error">Could not load the student’s chart view.</p><button type="button" onClick={state?.reload}>Try again</button></div>;
  if (!state?.data?.canSet) return null;
  return <div className="neck-handedness">
    <label>Student’s chart view<select disabled={state.busy} value={state.data.handedness ?? ""} onChange={event => void state.save((event.target.value || null) as NeckDiagramPreference["handedness"])}>
      <option value="">Not set (right-handed view)</option><option value="right">Right-handed</option><option value="left">Left-handed</option>
    </select></label>
    <p className="small muted">Saved for this student. Applies to their lesson, practice and printed charts, including future shared charts, until changed.</p>
    {state.busy && <p role="status">Saving chart view…</p>}
    {state.problem && <p role="alert" className="error">{state.problem}</p>}
  </div>;
}
